import { forwardRef, InputHTMLAttributes } from 'react'
import { useController } from 'react-hook-form'

import { UseCloneVnetExtensionResult } from '../logic/useCloneVnetExtension'
import { Form } from '@/ui/atoms/form/Form'
import { Switch } from '@/ui/atoms/switch/Switch'
import { cn } from '@/ui/utils/style'

export interface CloneVnetExtensionParams {
  cloneVnetExtension: UseCloneVnetExtensionResult
}

export function CloneVnetExtension({ cloneVnetExtension }: CloneVnetExtensionParams) {
  const { form, enabled, setEnabled } = cloneVnetExtension
  const { field } = useController({ control: form.control, name: 'vnetUrl' })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="typography-label-3 text-secondary">Clone Tenderly Virtual Testnet</div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>
      {enabled && (
        <Form {...form}>
          <VnetUrlInput
            {...field}
            placeholder="Virtual testnet dashboard URL"
            error={form.formState.errors.vnetUrl?.message}
          />
        </Form>
      )}
    </div>
  )
}

interface AddressInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error: string | undefined
  className?: string
  placeholder?: string
  postFix?: React.ReactNode
  label?: string
}

export const VnetUrlInput = forwardRef<HTMLInputElement, AddressInputProps>(
  ({ error, postFix, label, placeholder, className, ...rest }, ref) => {
    return (
      <div className="flex flex-col gap-2">
        {label && <div className="typography-label-3 text-secondary">{label}</div>}
        <div
          className={cn(
            'grid min-h-14 grid-cols-[1fr_auto] gap-2',
            'typography-label-4 sm:typography-label-2 text-primary',
            'rounded-sm border border-primary bg-secondary',
            'focus-within:border-brand-primary',
            'items-center rounded-sm pl-3 sm:pl-4',
            'cursor-text',
            error && 'border-error-200 bg-system-error-primary',
            className,
          )}
          onClick={(e) => {
            if (!(e.target as HTMLElement).closest('button')) {
              e.currentTarget.querySelector('input')?.focus()
            }
          }}
        >
          <input
            className={cn('flex w-full focus:outline-hidden')}
            ref={ref}
            placeholder={placeholder}
            type="text"
            inputMode="text"
            {...rest}
          />
          {postFix}
        </div>
        {error && <div className="typography-label-4 text-system-error-primary">{error}</div>}
      </div>
    )
  },
)
